import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useTabParam } from "../../lib/useTabParam";
import type { TeamMember } from "../../lib/types";
import PipelineView from "./PipelineView";
import { Link } from "react-router-dom";
import MyCommissionsTab from "./MyCommissionsTab";

// ---------------------------------------------------------------------------
// Session state persistence helpers (survives back-navigation)
// ---------------------------------------------------------------------------
const FILTER_KEY = "pipeline_filters";

function saveFilters(state: Record<string, string>) {
  try { sessionStorage.setItem(FILTER_KEY, JSON.stringify(state)); } catch {}
}
function loadFilters(): Record<string, string> | null {
  try {
    const raw = sessionStorage.getItem(FILTER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// Unified "My Pipeline" — adapts tabs based on role (RM / TL / ASM)
// ---------------------------------------------------------------------------

type TabKey = "deals" | "commissions";

interface TabDef {
  key: TabKey;
  label: string;
}

/** Roles that see the Closing phase in pipeline */
const CLOSING_ROLES = ["admin", "sales_director", "qc", "roof_director", "director_of_operations", "finance"];

/** Executive roles that get org-wide filters instead of personal pipeline */
const EXEC_ROLES = ["admin", "sales_director", "qc", "roof_director", "director_of_operations"];

// Aug 24, 2026 (Ship 2): the long-dead schedule/inbox/runtheplay branches were
// removed for real. CalendarTab lives on the Home schedule, the Inbox is the
// Notifications board, Run the Play is its own page at /run-the-play, and the
// legacy CommissionsTab/ExecCommissionsTab/RunThePlayTab components moved to
// LegacyCommissionsTabs.tsx + RunThePlayTab.tsx (RepBoardsTab still uses them).
const MY_PIPELINE_TABS = ["deals", "commissions"] as const;

export default function MyPipeline() {
  const { member, isSuperUser } = useAuth();
  const [tab, setTab] = useTabParam<TabKey>("tab", "deals", MY_PIPELINE_TABS);

  const role = member?.role;
  const isASM = role === "asm";
  const isTL = role === "team_lead";
  const isExec = (member && isSuperUser) || (role ? EXEC_ROLES.includes(role) : false);
  const showClosing = (member && isSuperUser) || (role ? CLOSING_ROLES.includes(role) : false);

  // Build role-specific tab list — must be called before early return (React hooks rule)
  const tabs: TabDef[] = useMemo(() => {
    if (isExec) {
      return [
        { key: "deals", label: "All Deals" },
        { key: "commissions", label: "Commissions" },
      ];
    }
    const t: TabDef[] = [{ key: "deals", label: "My Deals" }];
    t.push({ key: "commissions", label: "Commissions" });
    return t;
  }, [isASM, isExec]);

  if (!member) return null;

  const firstName = member.full_name?.split(" ")[0] ?? "My";

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-bardin-ink">
          {isExec ? "Pipeline Overview" : `${firstName}'s Pipeline`}
        </h1>
        <p className="text-sm text-slate-500">
          {isExec
            ? "Filter by ASM, team lead, or individual RM."
            : isASM
              ? "Pipeline, appointment review, and commissions."
              : isTL
                ? "Pipeline and commissions."
                : "Pipeline and commissions."}
        </p>
      </div>

      <div className="border-b border-slate-200 mb-4">
        <nav className="flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                "px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 whitespace-nowrap " +
                (tab === t.key
                  ? "text-bardin-teal border-bardin-teal"
                  : "text-slate-500 border-transparent hover:text-slate-700")
              }
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === "deals" && (
        <DealsTab
          member={member}
          isASM={isASM}
          isTL={isTL}
          isExec={isExec}
          showClosing={showClosing}
        />
      )}
      {/* Self-service commission board. RM/TL/ASM see their own personal +
          overrides + team rollup; exec/admin see the company-wide per-deal
          RM/TL/ASM breakdown. Same finance-board math either way. */}
      {tab === "commissions" && <MyCommissionsTab member={member} isExec={isExec} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Deals — PipelineView with team/member filters for TL/ASM
// ---------------------------------------------------------------------------

function DealsTab({
  member,
  isASM,
  isTL,
  isExec,
  showClosing,
}: {
  member: TeamMember;
  isASM: boolean;
  isTL: boolean;
  isExec: boolean;
  showClosing: boolean;
}) {
  const [allMembers, setAllMembers] = useState<TeamMember[] | null>(null);
  // Restore filter state from session storage (survives back-navigation)
  const saved = useRef(loadFilters());
  const [filterTeam, setFilterTeam] = useState<string>(saved.current?.filterTeam ?? "all");
  const [filterMember, setFilterMember] = useState<string>(saved.current?.filterMember ?? "all");
  // Executive-level cascading filters
  const [filterASM, setFilterASM] = useState<string>(saved.current?.filterASM ?? "all");
  const [filterTL, setFilterTL] = useState<string>(saved.current?.filterTL ?? "all");
  const [filterRM, setFilterRM] = useState<string>(saved.current?.filterRM ?? "all");

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    saveFilters({ filterTeam, filterMember, filterASM, filterTL, filterRM });
  }, [filterTeam, filterMember, filterASM, filterTL, filterRM]);

  // needsTeamData = isExec || isASM || isTL (used by team filter logic below)

  // Load team structure for filters (always load for RM/Split reassignment)
  useEffect(() => {
    supabase
      .from("team_roster")
      .select("*")
      .eq("is_active", true)
      .in("role", ["restore_manager", "team_lead", "asm"])
      .then(({ data }) => {
        if (data) setAllMembers(data as TeamMember[]);
      });
  }, []);

  // ---- Executive hierarchy (ASM → TL → RM) ----
  const execHierarchy = useMemo(() => {
    if (!isExec || !allMembers) return null;

    const asms = allMembers.filter((m) => m.role === "asm");
    const tls = allMembers.filter((m) => m.role === "team_lead");
    const rms = allMembers.filter((m) => m.role === "restore_manager");

    // Build hierarchy: ASM → TLs → RMs
    const hierarchy = asms.map((asm) => {
      const asmTLs = tls.filter((tl) => tl.team_lead_id === asm.id);
      return {
        asm,
        teams: asmTLs.map((tl) => ({
          tl,
          rms: rms.filter((rm) => rm.team_lead_id === tl.id),
        })),
      };
    });

    // TLs visible based on ASM filter
    const visibleTLs =
      filterASM === "all"
        ? tls
        : hierarchy.find((h) => h.asm.id === filterASM)?.teams.map((t) => t.tl) ?? [];

    // RMs visible based on TL filter (or ASM filter)
    // Include TLs and ASMs since they carry deals too
    let visibleRMs: TeamMember[];
    if (filterTL !== "all") {
      const tlMember = tls.find((t) => t.id === filterTL);
      visibleRMs = [
        ...(tlMember ? [tlMember] : []),
        ...rms.filter((rm) => rm.team_lead_id === filterTL),
      ];
    } else if (filterASM !== "all") {
      const asmEntry = hierarchy.find((h) => h.asm.id === filterASM);
      const asmMember = asms.find((a) => a.id === filterASM);
      visibleRMs = [
        ...(asmMember ? [asmMember] : []),
        ...(asmEntry ? asmEntry.teams.map((t) => t.tl) : []),
        ...(asmEntry ? asmEntry.teams.flatMap((t) => t.rms) : []),
      ];
    } else {
      visibleRMs = [...asms, ...tls, ...rms];
    }

    return { asms, tls, rms, hierarchy, visibleTLs, visibleRMs };
  }, [isExec, allMembers, filterASM, filterTL]);

  // ---- Executive: compute filtered IDs ----
  const execFilteredIds = useMemo(() => {
    if (!isExec || !allMembers) return null;
    const h = execHierarchy;
    if (!h) return null;

    // Specific RM selected
    if (filterRM !== "all") return [filterRM];

    // Specific TL selected → TL + their RMs
    if (filterTL !== "all") {
      const tlRMs = h.rms.filter((rm) => rm.team_lead_id === filterTL);
      return [filterTL, ...tlRMs.map((rm) => rm.id)];
    }

    // Specific ASM selected → ASM + their TLs + RMs under those TLs
    if (filterASM !== "all") {
      const entry = h.hierarchy.find((e) => e.asm.id === filterASM);
      if (!entry) return [filterASM];
      return [
        filterASM,
        ...entry.teams.map((t) => t.tl.id),
        ...entry.teams.flatMap((t) => t.rms.map((rm) => rm.id)),
      ];
    }

    // All → null means "no owner filter" so PipelineView shows every deal
    return null;
  }, [isExec, allMembers, execHierarchy, filterASM, filterTL, filterRM]);

  // Compute team hierarchy (ASM/TL mode — unchanged)
  const { teams, filteredIds, membersMap } = useMemo(() => {
    // Executive mode uses execFilteredIds instead
    if (isExec && allMembers) {
      const map: Record<string, { full_name: string }> = {};
      allMembers.forEach((m) => { map[m.id] = { full_name: m.full_name }; });
      return {
        teams: [] as { tlId: string; tlName: string; members: TeamMember[] }[],
        filteredIds: execFilteredIds,
        membersMap: map,
      };
    }

    if (!allMembers)
      return {
        teams: [] as { tlId: string; tlName: string; members: TeamMember[] }[],
        filteredIds: [member.id],
        membersMap: {} as Record<string, { full_name: string }>,
      };

    const map: Record<string, { full_name: string }> = {};
    allMembers.forEach((m) => {
      map[m.id] = { full_name: m.full_name };
    });

    if (isASM) {
      const tls = allMembers.filter((m) => m.team_lead_id === member.id);
      const teamList = tls.map((tl) => ({
        tlId: tl.id,
        tlName: tl.full_name,
        members: allMembers.filter((m) => m.team_lead_id === tl.id),
      }));

      let ids: string[];
      if (filterTeam === "all" && filterMember === "all") {
        ids = [
          member.id,
          ...tls.map((t) => t.id),
          ...teamList.flatMap((t) => t.members.map((m) => m.id)),
        ];
      } else if (filterTeam === "mine") {
        ids = [member.id];
      } else if (filterMember !== "all") {
        ids = [filterMember];
      } else if (filterTeam !== "all") {
        const team = teamList.find((t) => t.tlId === filterTeam);
        ids = team
          ? [team.tlId, ...team.members.map((m) => m.id)]
          : [member.id];
      } else {
        ids = [member.id];
      }

      return { teams: teamList, filteredIds: [...new Set(ids)], membersMap: map };
    }

    if (isTL) {
      const myRMs = allMembers.filter((m) => m.team_lead_id === member.id);
      let ids: string[];
      if (filterMember === "all") {
        ids = [member.id, ...myRMs.map((m) => m.id)];
      } else if (filterMember === "mine") {
        ids = [member.id];
      } else {
        ids = [filterMember];
      }
      return { teams: [], filteredIds: [...new Set(ids)], membersMap: map };
    }

    return { teams: [], filteredIds: [member.id], membersMap: map };
  }, [allMembers, member, isASM, isTL, isExec, execFilteredIds, filterTeam, filterMember]);

  // When ASM changes team filter, reset member filter
  const handleTeamChange = useCallback((val: string) => {
    setFilterTeam(val);
    setFilterMember("all");
  }, []);

  // Executive cascading resets
  const handleASMChange = useCallback((val: string) => {
    setFilterASM(val);
    setFilterTL("all");
    setFilterRM("all");
  }, []);

  const handleTLChange = useCallback((val: string) => {
    setFilterTL(val);
    setFilterRM("all");
  }, []);

  // Get members for the selected team (ASM member dropdown)
  const teamMembers = useMemo(() => {
    if (!isASM || !allMembers) return [];
    if (filterTeam === "all" || filterTeam === "mine") {
      return allMembers.filter((m) => m.id !== member.id);
    }
    const team = teams.find((t) => t.tlId === filterTeam);
    return team ? [allMembers.find((m) => m.id === team.tlId)!, ...team.members] : [];
  }, [isASM, allMembers, filterTeam, teams, member.id]);

  // TL's RMs for their member dropdown
  const tlMembers = useMemo(() => {
    if (!isTL || !allMembers) return [];
    return allMembers.filter((m) => m.team_lead_id === member.id);
  }, [isTL, allMembers, member.id]);

  const showOwner = isExec || ((isASM || isTL) && filterMember === "all");

  return (
    <div>
      {/* Executive filters: ASM → TL → RM */}
      {isExec && execHierarchy && (
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-500 uppercase">
              ASM
            </label>
            <select
              value={filterASM}
              onChange={(e) => handleASMChange(e.target.value)}
              className="px-3 py-1.5 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
            >
              <option value="all">All</option>
              {execHierarchy.asms.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.full_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-500 uppercase">
              Team Lead
            </label>
            <select
              value={filterTL}
              onChange={(e) => handleTLChange(e.target.value)}
              className="px-3 py-1.5 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
            >
              <option value="all">All</option>
              {execHierarchy.visibleTLs.map((tl) => (
                <option key={tl.id} value={tl.id}>
                  {tl.full_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-500 uppercase">
              RM
            </label>
            <select
              value={filterRM}
              onChange={(e) => setFilterRM(e.target.value)}
              className="px-3 py-1.5 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
            >
              <option value="all">All</option>
              {execHierarchy.visibleRMs.map((rm) => (
                <option key={rm.id} value={rm.id}>
                  {rm.full_name}{rm.role === "asm" ? " (ASM)" : rm.role === "team_lead" ? " (TL)" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Active filter count */}
          {(filterASM !== "all" || filterTL !== "all" || filterRM !== "all") && (
            <button
              onClick={() => { handleASMChange("all"); }}
              className="px-3 py-1.5 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* ASM / TL filters (non-exec) */}
      {!isExec && (isASM || isTL) && (
        <div className="flex flex-wrap gap-3 mb-4">
          {isASM && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-500 uppercase">
                Team
              </label>
              <select
                value={filterTeam}
                onChange={(e) => handleTeamChange(e.target.value)}
                className="px-3 py-1.5 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
              >
                <option value="all">All Teams</option>
                <option value="mine">My Deals Only</option>
                {teams.map((t) => (
                  <option key={t.tlId} value={t.tlId}>
                    {t.tlName}'s Team
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-500 uppercase">
              Member
            </label>
            <select
              value={filterMember}
              onChange={(e) => setFilterMember(e.target.value)}
              className="px-3 py-1.5 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
            >
              <option value="all">
                {isTL ? "All (me + team)" : "All Members"}
              </option>
              {isTL && <option value="mine">My Deals Only</option>}
              {(isASM ? teamMembers : tlMembers).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Run the Play quick-launch */}
          <div className="ml-auto">
            <Link
              to="/run-the-play"
              className="px-3 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
              Run the Play
            </Link>
          </div>
        </div>
      )}

      {/* RM view: just the quick-launch button */}
      {!isExec && !isASM && !isTL && (
        <div className="flex justify-end mb-4">
          <Link
            to="/run-the-play"
            className="px-3 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
            Run the Play
          </Link>
        </div>
      )}

      <PipelineView
        ownerIds={filteredIds}
        showClosing={showClosing}
        myId={member.id}
        showOwner={showOwner}
        members={membersMap}
        userRole={member.role}
      />
    </div>
  );
}

